// src/pages/auth/ForgotPasswordPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Mail, Lock, Loader, AlertCircle, ArrowRight } from 'lucide-react';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  const { resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    const result = await resetPassword(email);
    
    if (result.success) {
      setMessage(result.message);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        
        {/* חזרה לדף הבית */}
        <Link 
          to="/" 
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 group"
        >
          <ArrowRight className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">חזרה לדף הבית</span>
        </Link>

        {/* כרטיס איפוס סיסמה */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          
          {/* כותרת */}
          <div className="bg-gradient-to-r from-gray-600 to-blue-600 px-8 py-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">איפוס סיסמה</h1>
            <p className="text-blue-100">נשלח לך קישור לאיפוס סיסמה</p>
          </div>

          {/* תוכן הטופס */}
          <div className="p-8">
            
            {/* הודעות */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            {message && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <span className="text-green-700 text-sm">{message}</span>
              </div>
            )}

            {!message ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    כתובת אימייל
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-4 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300"
                      placeholder="example@gmail.com"
                      dir="ltr"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`
                    w-full py-3 px-4 rounded-xl text-lg font-bold
                    flex items-center justify-center gap-3
                    ${loading 
                      ? 'bg-gray-300 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-gray-600 to-blue-600 hover:from-gray-700 hover:to-blue-700'
                    }
                    text-white transition-all duration-300
                    transform hover:scale-105 active:scale-95
                    shadow-lg hover:shadow-xl
                  `}
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>שולח...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      <span>שלח קישור לאיפוס</span>
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="text-center">
                <p className="text-gray-600 mb-6">
                  בדוק את תיבת המייל שלך וללחץ על הקישור לאיפוס הסיסמה
                </p>
                <Link
                  to="/auth/login"
                  className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-300 transform hover:scale-105"
                >
                  <span>חזרה להתחברות</span>
                  <ArrowRight className="w-4 h-4 mr-2" />
                </Link>
              </div>
            )}

            {/* קישורים נוספים */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center space-y-3">
              <Link
                to="/auth/login"
                className="block text-blue-600 hover:text-blue-700 hover:underline text-sm"
              >
                חזרה להתחברות
              </Link>
              <Link
                to="/auth/register"
                className="block text-purple-600 hover:text-purple-700 hover:underline text-sm"
              >
                הרשמה חדשה
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;